<template>
    <modal
        :saveAction="save"
        :title="getTitle()"
        :id="id"
        :actionButtons="false"
        :scrollable="false"
        :closeButton="false"
        size="full"
        @shown="shown()"
        @hidden="hidden()"
        lazy
    >
        <div class="container-fluid">
            <div class="row" v-if="item.label">
                <div class="col-12 d-flex justify-content-center align-items-center">
                    <template v-if="mimetypeDocs.includes(item.mimetype)">
                        <iframe
                            :src="'https://docs.google.com/gview?url='+item.url+'&embedded=true'"
                            style="width:100%; height: calc( 100vh - 190px );"
                            frameborder="0"
                        ></iframe>
                    </template>
                    <template v-if="mimetypeImages.includes(item.mimetype) ">
                        <img
                            :src="item.url"
                            class="media-view-img d-block d-md-none"
                            crossorigin="anonymous"
                            alt=""
                        />
                        <tui-image-editor
                            class="d-none d-md-block"
                            ref="tuiImageEditor"
                            :include-ui="useDefaultUI"
                            :options="options"
                        ></tui-image-editor>
                    </template>
                </div>
            </div>
        </div>
        <template slot="modal-footer">
            <div class="card-footer row">
                <div class="col-auto">
                    <button type="button" @click="close" class="btn btn-white mr-2">{{ $t('Close') }}</button>
                </div>
                <div class="col"></div>
                <div class="col-auto">
                    <button class="btn btn-success" @click="download()">
                        <i class="fe fe-download"></i>
                    </button>
                    <confirmable-button
                        style="display:inline;"
                        buttonClass="btn btn-danger d-lg-inline-block ml-2"
                        @confirm="deleteFile"
                    >
                        <i class="fe fe-trash"></i>
                    </confirmable-button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "@/components/Modal.vue";
import Avatar from '@/components/Avatar.vue'
import PrettyBytes from '@/filters/pretty-bytes.ts';
import {mapActions, mapGetters, mapState} from 'vuex';
import 'tui-color-picker/dist/tui-color-picker.css';
import 'tui-image-editor/dist/tui-image-editor.css';
import {ImageEditor} from '@toast-ui/vue-image-editor';
import ConfirmableButton from '@/components/ConfirmableButton';

const whiteTheme = {
    'common.bi.image': 'inherit',
    'common.bisize.width': '251px',
    'common.bisize.height': '21px',
    'common.backgroundImage': '',
    'common.backgroundColor': '#fff',
    'common.border': '1px solid #c1c1c1',

    // header
    'header.backgroundImage': 'none',
    'header.backgroundColor': 'transparent',
    'header.border': '0px',

    // load button
    'loadButton.backgroundColor': '#fff',
    'loadButton.border': '1px solid #ddd',
    'loadButton.color': '#222',
    'loadButton.fontFamily': "inherit",
    'loadButton.fontSize': '12px',

    // download button
    'downloadButton.backgroundColor': '#fdba3b',
    'downloadButton.border': '1px solid #fdba3b',
    'downloadButton.color': '#fff',
    'downloadButton.fontFamily': "inherit",
    'downloadButton.fontSize': '12px',

    // main icons
    'menu.normalIcon.color': '#8a8a8a',
    'menu.activeIcon.color': '#555555',
    'menu.disabledIcon.color': '#434343',
    'menu.hoverIcon.color': '#e9e9e9',
    'menu.iconSize.width': '24px',
    'menu.iconSize.height': '24px',

    // submenu icons
    'submenu.normalIcon.color': '#8a8a8a',
    'submenu.activeIcon.color': '#555555',
    'submenu.iconSize.width': '32px',
    'submenu.iconSize.height': '32px',

    // submenu primary color
    'submenu.backgroundColor': 'transparent',
    'submenu.partition.color': '#e5e5e5',

    // submenu labels
    'submenu.normalLabel.color': '#858585',
    'submenu.normalLabel.fontWeight': 'normal',
    'submenu.activeLabel.color': '#000',
    'submenu.activeLabel.fontWeight': 'normal',

    // checkbox style
    'checkbox.border': '1px solid #ccc',
    'checkbox.backgroundColor': '#fff',

    // rango style
    'range.pointer.color': '#333',
    'range.bar.color': '#ccc',
    'range.subbar.color': '#606060',

    'range.disabledPointer.color': '#d3d3d3',
    'range.disabledBar.color': 'rgba(85,85,85,0.06)',
    'range.disabledSubbar.color': 'rgba(51,51,51,0.2)',

    'range.value.color': '#000',
    'range.value.fontWeight': 'normal',
    'range.value.fontSize': '11px',
    'range.value.border': '0',
    'range.value.backgroundColor': '#f5f5f5',
    'range.title.color': '#000',
    'range.title.fontWeight': 'lighter',

    // colorpicker style
    'colorpicker.button.border': '0px',
    'colorpicker.title.color': '#000',
};

export default {
    props: ['id', 'item'],
    components: {
        Modal,
        Avatar,
        'tui-image-editor': ImageEditor,
        ConfirmableButton
    },
    data() {
        return {
            title: 'View media',
            useDefaultUI: true,
            options: {
                usageStatistics: false,
                includeUI: {
                    loadImage: {
                        path: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                        name: 'Blank'
                    },
                    theme: whiteTheme,
                    menuBarPosition: 'left',
                    //menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'icon', 'text', 'mask'],
                    menu: ['flip', 'rotate', 'draw', 'text', 'filter'],
                    initMenu: '',
                },
                cssMaxWidth: window.clientWidth,
                cssMaxHeight: window.clientHeight,
                selectionStyle: {
                    cornerSize: 20,
                    rotatingPointOffset: 70
                }
            },
            mimetypeImages: ['image/x-icon', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/tiff'],
            mimetypeDocs: [
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/pdf',
                'application/msword'
            ],
            currentPage: 0,
            pageCount: 0,
        }
    },
    computed: {
        size() {
            const value = Number(this.item.size);
            return PrettyBytes(value);
        },
    },
    mounted() {
        this.title = this.item.label;
    },
    created() {
    },
    methods: {
        ...mapActions('media', ['remove']),
        download() {
            window.open(this.item.url);
        },
        deleteFile() {
            this.$root.$emit('bv::hide::modal', this.id);
            this.remove(this.item._id).then(result => {
                this.$toasted.global.info({
                    message: this.$t('Item has been deleted')
                });
            });
        },
        save() {

        },
        getTitle() {
            return this.item.label;
        },
        close() {
            this.$root.$emit('bv::hide::modal', this.id);
        },
        shown() {
            if (this.mimetypeImages.includes(this.item.mimetype))
                this.$refs.tuiImageEditor.invoke('loadImageFromURL', this.item.url, this.item.label);
        },
        hidden() {
        },
    }
}
</script>

<style lang="scss">
.media-view-img {
    width: 100%;
    height: 100%;
    max-height: calc(var(--calculated-vh, 1vh) * 100 - 200px);
}

.tui-image-editor-container {
    height: calc(var(--calculated-vh, 1vh) * 100 - 200px) !important;

    .tui-image-editor-header-logo {
        display: none;
    }

    .tui-image-editor-header-buttons {
        display: none;
    }

    .tui-image-editor-item {
        background: rgba(255, 255, 255, 0.7) !important;
    }

    .tui-image-editor-controls {
        background-color: transparent !important;
    }

    .tui-image-editor-main-container {
        border: none !important;
        width: 100% !important;
        left: 0 !important;
    }

    .tui-image-editor-submenu {
        border: 1px solid #EDF2F9;
        left: 30%;
        background-color: white;
    }
}
</style>
